import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { has, toggle } = useWishlist() || {};
  const navigate = useNavigate();

  useEffect(() => {
    setData(null); setSize(null); setColor(null); setImgIdx(0);
    client.get(`/products/${id}`).then(r => setData(r.data));
  }, [id]);

  if (!data) return <div className="container-editorial py-24"><p className="label-uppercase">Loading…</p></div>;
  const { product, vendor } = data;

  const handleAdd = async () => {
    if (!user) { toast.error("Please sign in to add items"); navigate("/login?redirect=/product/"+id); return; }
    if (product.sizes?.length && !size) { toast.error("Please pick a size"); return; }
    if (product.colors?.length && !color) { toast.error("Please pick a color"); return; }
    try {
      await addItem(product.id, 1, size, color);
      toast.success(`${product.name} added to bag`);
    } catch { toast.error("Could not add to bag"); }
  };

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-7">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-2 hidden md:flex flex-col gap-4">
              {product.images?.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                        className={`aspect-[3/4] overflow-hidden ${i===imgIdx ? "ring-2 ring-[#B24C38]" : ""}`}
                        data-testid={`product-thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
            <div className="col-span-12 md:col-span-10">
              <div className="aspect-[3/4] overflow-hidden bg-[#EAE5D9]">
                <img src={product.images?.[imgIdx]} alt={product.name} className="w-full h-full object-cover"/>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-5">
          <Link to={`/vendor/${vendor?.slug}`} className="label-uppercase text-[#B24C38] link-underline" data-testid="product-vendor-link">
            {vendor?.name}
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tighter leading-none mt-3" data-testid="product-name">
            {product.name}
          </h1>
          <p className="font-serif text-3xl mt-4" data-testid="product-price">${product.price.toFixed(2)}</p>
          <p className="text-[#4A4A4A] leading-relaxed mt-6">{product.description}</p>

          {product.sizes?.length > 0 && (
            <div className="mt-10">
              <p className="label-uppercase mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)}
                          data-testid={`product-size-${s}`}
                          className={`min-w-[52px] py-2 px-4 border text-sm ${size===s ? "bg-[#111] text-[#F7F5F0] border-[#111]" : "border-[#111]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <p className="label-uppercase mb-3">Color {color && <span className="text-[#4A4A4A] normal-case tracking-normal">· {color}</span>}</p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map(c => (
                  <button key={c} onClick={() => setColor(c)}
                          data-testid={`product-color-${c.replace(/\s+/g,'-').toLowerCase()}`}
                          className={`py-2 px-4 border text-sm ${color===c ? "bg-[#111] text-[#F7F5F0] border-[#111]" : "border-[#111]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 space-y-3">
            <button onClick={handleAdd} className="btn-terracotta w-full justify-center" data-testid="add-to-cart-button">
              Add to Bag
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!user) { toast.error("Sign in to save"); navigate("/login?redirect=/product/"+id); return; }
                const already = has && has(product.id, size);
                try { await toggle(product.id, size); toast.success(already ? "Removed from wishlist" : "Saved to wishlist"); }
                catch { toast.error("Could not update wishlist"); }
              }}
              data-testid="wishlist-btn"
              className="btn-outline-ink w-full justify-center flex items-center gap-2">
              <Heart className={`w-4 h-4 ${has && has(product.id, size) ? "fill-[#B24C38] text-[#B24C38]" : ""}`}/>
              {has && has(product.id, size) ? "Saved · Restock alerts on" : "Save & get restock alerts"}
            </button>
            <p className="text-xs text-[#4A4A4A]">Ships in 3–5 business days · Free returns within 30 days</p>
          </div>

          <div className="mt-12 pt-8 border-t border-[#EAE5D9] space-y-3">
            <div className="flex justify-between text-sm"><span className="label-uppercase">Vendor</span><Link to={`/vendor/${vendor?.slug}`} className="link-underline">{vendor?.name}</Link></div>
            <div className="flex justify-between text-sm"><span className="label-uppercase">Category</span><span>{product.category}</span></div>
            {product.tags?.length > 0 && (
              <div className="flex justify-between text-sm"><span className="label-uppercase">Style</span><span>{product.tags.join(", ")}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
